import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Rocket, Undo2 } from 'lucide-react'
import { toast } from 'sonner'
import { SaveIndicator } from '@/components/SaveIndicator'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { platformApi } from '../api'
import DesignTab, { type Selection } from './builder/DesignTab'
import PreviewTab from './builder/PreviewTab'
import ProblemsPanel from './builder/ProblemsPanel'
import PublishDialog from './builder/PublishDialog'
import AiReviewTab from './builder/AiReviewTab'
import RulesTab from './builder/RulesTab'
import VersionsTab from './builder/VersionsTab'
import DetailsTab from './builder/DetailsTab'
import { allFieldKeys, describeShort, locateProblem, validateDefinition, type Problem } from './builder/model'
import { useBuilderDraft } from './builder/useBuilderDraft'

type Tab = 'design' | 'rules' | 'ai' | 'preview' | 'details' | 'versions'

const statusVariant = { published: 'default', draft: 'secondary', archived: 'outline' } as const

export default function FormBuilderPage({ formId }: { formId: string }) {
    const navigate = useNavigate()
    const d = useBuilderDraft(formId)
    const [tab, setTab] = useState<Tab>('design')
    const [selection, setSelection] = useState<Selection>({ pageId: null, index: null })
    const [externalFocus, setExternalFocus] = useState(0)
    const [publishOpen, setPublishOpen] = useState(false)
    const [discardOpen, setDiscardOpen] = useState(false)
    const [discarding, setDiscarding] = useState(false)
    const [versionsKey, setVersionsKey] = useState(0)

    // Start on the first page once loaded.
    useEffect(() => {
        if (!d.loading && selection.pageId === null && d.def.pages.length > 0) setSelection({ pageId: d.def.pages[0].id, index: null })
    }, [d.loading, d.def.pages, selection.pageId])

    // Instant client hints, merged with the (authoritative) server list; identical wording de-duplicates.
    const problems = useMemo<Problem[]>(() => {
        const out: Problem[] = []
        const seen = new Set<string>()
        for (const p of validateDefinition(d.def)) { if (!seen.has(p.message)) { seen.add(p.message); out.push(p) } }
        for (const m of d.serverProblems) {
            if (seen.has(m)) continue
            seen.add(m)
            const loc = locateProblem(d.def, m)
            out.push({ message: m, pageId: loc?.pageId, key: loc?.key })
        }
        return out
    }, [d.def, d.serverProblems])

    const problemKeys = useMemo(() => new Set(problems.flatMap((p) => (p.key ? [p.key] : []))), [problems])
    const publishedKeys = useMemo(
        () => (d.published ? allFieldKeys({ pages: d.published.definition?.pages ?? [], rules: [] }) : new Set<string>()),
        [d.published],
    )

    const jump = (p: Problem) => {
        if (!p.pageId) return
        const page = d.def.pages.find((x) => x.id === p.pageId)
        const index = p.key && page ? page.fields.findIndex((f) => f.key === p.key) : -1
        setTab('design')
        setSelection({ pageId: p.pageId, index: index >= 0 ? index : null })
        if (index >= 0) setExternalFocus((n) => n + 1)
    }

    if (d.loading && !d.form) {
        return <div className="flex flex-col gap-4"><Skeleton className="h-10 w-80" /><Skeleton className="h-96 w-full" /></div>
    }
    if (d.loadError || !d.form) {
        return (
            <div className="mx-auto max-w-xl py-8">
                <Alert variant="destructive">
                    <AlertTitle>Could not load this form</AlertTitle>
                    <AlertDescription>{d.loadError ?? 'Unknown error'}</AlertDescription>
                </Alert>
                <Button className="mt-4" variant="outline" onClick={() => void navigate({ to: '/manage/forms' })}><ArrowLeft data-icon="inline-start" /> Back to forms</Button>
            </div>
        )
    }

    const form = d.form
    const saving = d.saveStatus === 'saving'
    const canPublish = d.hasDraft && problems.length === 0 && !d.dirty && !saving && d.saveStatus !== 'error'

    const openPublish = async () => {
        const ok = await d.flush()
        if (!ok) { toast.error('Could not save the draft, so it was not published.'); return }
        setPublishOpen(true)
    }
    const publish = async (changelog: string) => {
        try {
            const ok = await d.flush()
            if (!ok) throw new Error('Could not save the draft first.')
            const v = await platformApi.publish(formId, changelog)
            toast.success(`Published version ${v.version}`)
            setPublishOpen(false)
            setVersionsKey((n) => n + 1)
            await d.reload()
        } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Publish failed')
        }
    }
    const discard = async () => {
        setDiscarding(true)
        try {
            await d.abandon()
            await platformApi.discardDraft(formId)
            toast.success('Draft discarded')
            setDiscardOpen(false)
            setSelection({ pageId: null, index: null })
            await d.reload()
        } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not discard the draft')
        } finally {
            setDiscarding(false)
        }
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="sticky top-14 z-30 -mx-4 flex flex-col gap-3 border-b bg-background px-4 py-3 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <Button variant="ghost" size="icon-sm" aria-label="Back to forms" onClick={() => void navigate({ to: '/manage/forms' })}><ArrowLeft /></Button>
                    <div className="flex min-w-0 flex-col">
                        <h1 className="truncate text-lg font-semibold leading-tight">{form.name}</h1>
                        <p className="truncate text-xs text-muted-foreground">
                            {d.base.startsWith('active:') ? `Started from version ${d.base.slice(7)}` : d.base === 'empty' ? 'New form' : 'Draft'}
                        </p>
                    </div>
                    <Badge variant={statusVariant[form.status] ?? 'outline'} className="capitalize">{form.status}</Badge>
                    {form.active_version && <Badge variant="outline">v{form.active_version}</Badge>}
                    {form.draft_version && <Badge variant="secondary">Open draft</Badge>}
                    <div className="ml-auto flex flex-wrap items-center gap-2">
                        <span className="flex items-center gap-2 text-xs text-muted-foreground" aria-live="polite">
                            {d.dirty && !saving ? 'Unsaved changes' : d.saveStatus === 'idle' && d.hasDraft ? 'Draft saved' : null}
                            <SaveIndicator status={d.saveStatus} />
                        </span>
                        <Button variant="outline" size="sm" disabled={!d.hasDraft && !d.dirty} onClick={() => setDiscardOpen(true)}>
                            <Undo2 data-icon="inline-start" /> Discard draft
                        </Button>
                        <Button size="sm" disabled={!canPublish} onClick={() => void openPublish()}>
                            <Rocket data-icon="inline-start" /> Publish
                        </Button>
                    </div>
                </div>
                <ProblemsPanel problems={problems} onJump={jump} />
            </div>

            <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
                <TabsList>
                    <TabsTrigger value="design">Design</TabsTrigger>
                    <TabsTrigger value="rules">Rules{d.def.rules.length > 0 && ` (${d.def.rules.length})`}</TabsTrigger>
                    <TabsTrigger value="ai">AI review{d.def.ai?.disabled && ' (off)'}</TabsTrigger>
                    <TabsTrigger value="preview">Preview</TabsTrigger>
                    <TabsTrigger value="details">Details</TabsTrigger>
                    <TabsTrigger value="versions">Versions</TabsTrigger>
                </TabsList>
                <TabsContent value="design" className="pt-3">
                    <DesignTab
                        def={d.def} edit={d.edit} selection={selection} setSelection={setSelection}
                        publishedKeys={publishedKeys} publishedVersion={d.published?.version}
                        problemKeys={problemKeys} externalFocus={externalFocus}
                    />
                </TabsContent>
                <TabsContent value="rules" className="pt-3"><RulesTab def={d.def} edit={d.edit} /></TabsContent>
                <TabsContent value="ai" className="pt-3"><AiReviewTab def={d.def} edit={d.edit} /></TabsContent>
                <TabsContent value="preview" className="pt-3"><PreviewTab def={d.def} /></TabsContent>
                <TabsContent value="details" className="pt-3"><DetailsTab form={form} onSaved={d.setForm} /></TabsContent>
                <TabsContent value="versions" className="pt-3"><VersionsTab formId={formId} refreshKey={versionsKey} /></TabsContent>
            </Tabs>

            <PublishDialog open={publishOpen} onOpenChange={setPublishOpen} summary={describeShort(d.def) + '.'} onPublish={publish} />

            <Dialog open={discardOpen} onOpenChange={(o) => { if (!discarding) setDiscardOpen(o) }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Discard the open draft?</DialogTitle>
                        <DialogDescription>
                            All unpublished edits are lost. The builder reloads from {form.active_version ? `the published version (v${form.active_version})` : 'a blank form'}.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" disabled={discarding} onClick={() => setDiscardOpen(false)}>Keep editing</Button>
                        <Button variant="destructive" disabled={discarding} onClick={() => void discard()}>
                            {discarding && <Spinner data-icon="inline-start" />} Discard draft
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
